import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import type {
    GroupBase,
    OptionProps,
    SelectComponentsConfig,
    StylesConfig,
} from 'react-select';
import { components } from 'react-select';

import type { PopperProps } from '@mui/material/Popper';
import Popper from '@mui/material/Popper';
import Tooltip from '@mui/material/Tooltip';
// material-ui
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { createGenerateClassName, StylesProvider } from '@mui/styles';
import { BaseSelection, Editor, Element, Node, Range, Transforms } from 'slate';
import { ReactEditor, useFocused, useSlate } from 'slate-react';
import { v4 as uuid } from 'uuid';

import IconWrapper from '../../../../components/componentTypesIcon/ComponentTypesIconWrapper';
// components
import SearchableSelect, {
    SearchableSelectType,
} from '../../../../components/inputs/searchableSelect';
import Divider from '../../../../components/shared/Divider';
// hooks
import styled from 'styled-components';

// theme
import aampeTheme from '../../../../constants/aampeTheme';
import { FormulaType } from '../../../../constants/formulaType';
// constants
import ICONS from '../../../../constants/icons';

// helpers
import { getEmailFieldData } from '../../../../helpers/formulaHelpers';

import useComponentTypesQuery from '../../../../hooks/component-types/useComponentTypesQuery';

import {
    ComponentType,
    ComponentTypeMap,
} from '../../../../models/component-types-model';
import {
    ElementKind,
    LinkElement,
    SectionElement,
    SectionType,
    SlateElementsData,
} from '../../../../models/editor.model';

import { FormulaCreateType } from '../../../../views/formulaCreate/FormulaCreateMainV2';
import { extractLinkInfo } from '../../../../views/formulaCreate/messagePreview/email/email.helper';

import { APP_CONTAINER_ID } from '../../../../AppWithSession';
import { getCustomStyles } from '../../Sidebar/VariantTypeDropdown';
import variantMethods from '../variantMethods';
import AddLinkModal from './AddLinkModal';
import HoveredComponentData from './HoveredComponentData';

const getStyles = (
    componentTypesData: ComponentTypeMap,
): StylesConfig<
    SearchableSelectType,
    boolean,
    GroupBase<SearchableSelectType>
> => ({
    ...(getCustomStyles(componentTypesData) as StylesConfig<
        SearchableSelectType,
        boolean,
        GroupBase<SearchableSelectType>
    >),
    container: (provided) => ({
        ...provided,
        right: 0,
    }),
    menu: (provided) => ({
        ...provided,
        border: 'none',
        boxShadow: 'none',
        margin: aampeTheme.spacing(0),
    }),
    option: (provided) => ({
        ...provided,
        borderRadius: aampeTheme.borderRadius.default,
        padding: aampeTheme.spacing(1, 2),
        backgroundColor: aampeTheme.colors.white,

        '&:hover': {
            backgroundColor: aampeTheme.colors.gray25,
            borderRadius: aampeTheme.borderRadius.default,
        },
    }),
});

const Option = (props: OptionProps<SearchableSelectType, boolean>) => {
    const { data: componentTypes } = useComponentTypesQuery();
    const currentComponentType = componentTypes?.map[
        props.data.value
    ] as ComponentType;

    const TooltipTheme = createTheme({
        components: {
            MuiTooltip: {
                styleOverrides: {
                    tooltip: {
                        backgroundColor: aampeTheme.colors.white,
                        padding: aampeTheme.spacing(2),
                        border: `2px solid ${currentComponentType?.base_color}`,
                        maxWidth: '320px',
                    },
                },
            },
        },
    });

    return (
        <components.Option {...props}>
            <StylesProvider
                generateClassName={createGenerateClassName({
                    seed: `tooltip-${props.data.value}`,
                })}
            >
                <ThemeProvider theme={TooltipTheme}>
                    <Tooltip title={props.data.info ?? ''} placement="right">
                        <OptionContent>
                            <IconWrapper dataKey={props.data.value as string} />
                            <OptionLabel>{props.label}</OptionLabel>
                        </OptionContent>
                    </Tooltip>
                </ThemeProvider>
            </StylesProvider>
        </components.Option>
    );
};

interface CreateComponentsDropdownProps {
    createFormula: FormulaCreateType;
    formulaId?: number;
    formulaType: FormulaType;
    isLinkModalOpen?: boolean;
    setIsLinkModalOpen: (value: boolean) => void;
    selectedElement?: { [key: string]: any };
}

const CreateComponentsDropdown: FC<
    React.PropsWithChildren<CreateComponentsDropdownProps>
> = ({
    createFormula,
    formulaId,
    formulaType,
    isLinkModalOpen,
    setIsLinkModalOpen,
    selectedElement,
}) => {
    const inFocus = useFocused();
    const editor = useSlate();
    const { selection } = editor;
    const [anchorEl, setAnchorEl] = useState<PopperProps['anchorEl']>(null);
    const [linkId, setLinkId] = useState<string | undefined>(
        selectedElement?.elementId,
    );
    const { data: componentTypes } = useComponentTypesQuery();

    const getComponentTypeId = useCallback(
        (dataKey: string) => {
            const componentType = componentTypes?.map[dataKey];
            if (componentType) {
                return componentType.id;
            }
            return undefined;
        },
        [componentTypes],
    );

    const isEditMode = useMemo<boolean>(() => {
        if (selectedElement && selectedElement.kind === ElementKind.link) {
            return extractLinkInfo(selectedElement?.children?.[0]?.text)
                .isAnchorTag;
        }
        return false;
    }, [selectedElement]);

    const isLinkCreationAllowed = useMemo<boolean>(() => {
        if (formulaType === FormulaType.email) {
            if (selection) {
                const { anchor } = selection;
                const elementPath = Array.from(anchor.path).slice(0, -1);
                const node = Node.get(editor, elementPath) as SectionElement;
                return !SlateElementsData[node?.sectionType]?.hideLinkCreation;
            }
            return true;
        }
        return false;
    }, [selection, formulaType, editor]);

    useEffect(() => {
        if (selectedElement) {
            setLinkId(selectedElement?.elementId);
        }
    }, [selectedElement]);

    const isOpen = Boolean(anchorEl);

    const handleComponentDropdownClose = () => {
        setAnchorEl(null);
    };

    // Logic for creating virtual dom element related to selection range
    const updateAnchorEl = useCallback(() => {
        const getBoundingClientRect = () => {
            const { selection: currentSelection } = editor;
            return ReactEditor.toDOMRange(
                editor,
                currentSelection!,
            ).getBoundingClientRect();
        };

        setAnchorEl({
            clientWidth: getBoundingClientRect().width,
            clientHeight: getBoundingClientRect().height,
            getBoundingClientRect,
        });
    }, [editor]);

    const getInputType = useCallback<(node: any) => SectionType | ElementKind>(
        (node) => {
            if (formulaType === FormulaType.contentCards) {
                return [
                    SectionType.buttonLink,
                    SectionType.destination_link_on_image_click,
                ].includes(node.sectionType)
                    ? node.sectionType
                    : node.kind;
            }
            if (formulaType === FormulaType.surface) {
                return [
                    SectionType.surface_button_action,
                    SectionType.surface_button_text,
                ].includes(node.sectionType)
                    ? node.sectionType
                    : node.kind;
            }
            return node.kind;
        },
        [formulaType],
    );

    // Open dropdown if there is a selection
    useEffect(() => {
        if (
            !selection ||
            !inFocus ||
            Range.isCollapsed(selection) ||
            Editor.string(editor, selection)?.trim() === ''
        ) {
            handleComponentDropdownClose();
            return;
        }

        const { anchor, focus } = selection;
        const elementPath = Array.from(anchor.path).slice(0, -1);
        const node = Node.get(editor, elementPath);

        const isNoSelectionOverlap =
            anchor.path.toString() === focus.path.toString();

        if (
            !(
                isNoSelectionOverlap &&
                !Editor.isEditor(node) &&
                Element.isElement(node) &&
                SlateElementsData[getInputType(node)]?.allowVariantCreation &&
                'sectionType' in node &&
                getEmailFieldData(node.sectionType)?.allowVariantCreation
            )
        ) {
            handleComponentDropdownClose();
        } else {
            updateAnchorEl();
        }
    }, [selection, editor, inFocus, updateAnchorEl, getInputType]);

    // Listen for scroll if dropdown is open (to adjust position)
    useEffect(() => {
        const appElement = document.getElementById(APP_CONTAINER_ID);
        if (anchorEl) {
            appElement!.addEventListener('scroll', updateAnchorEl, {
                passive: true,
            });
        } else {
            appElement!.removeEventListener('scroll', updateAnchorEl);
        }
        return () => appElement!.removeEventListener('scroll', updateAnchorEl);
    }, [anchorEl, updateAnchorEl]);

    const createVariant = (id: number, variantType: string) => {
        variantMethods.toggleVariant(
            editor,
            variantType,
            getComponentTypeId(variantType),
        );
        handleComponentDropdownClose();
    };

    const toggleVariant = (value: SearchableSelectType) => {
        if (!formulaId) {
            createFormula({}, (newFormulaId) =>
                createVariant(newFormulaId as number, value.value as string),
            );
        } else {
            createVariant(formulaId, value.value as string);
        }
    };

    const variantTypes = useMemo<SearchableSelectType[]>(
        () =>
            (componentTypes?.list ?? [])
                .filter(
                    (componentTypes) =>
                        !componentTypes?.additional_metadata?.is_image &&
                        !componentTypes?.archived_at,
                )
                .map((componentType) => ({
                    value: componentType.data_key,
                    label: componentType.name,
                    info: (
                        <HoveredComponentData componentType={componentType} />
                    ),
                })),
        [componentTypes],
    );

    const customComponents = useMemo(() => {
        const selectComponents: SelectComponentsConfig<
            SearchableSelectType,
            boolean,
            GroupBase<SearchableSelectType>
        > = { Option, IndicatorSeparator: () => null };
        selectComponents['Input'] = () => null;
        selectComponents['Control'] = () => null;
        selectComponents['SingleValue'] = () => null;
        return selectComponents;
    }, []);

    const editorSelection = useMemo<BaseSelection>(
        () => editor.selection,
        [editor.selection],
    );

    const addLinkHandler = useCallback(() => {
        if (editorSelection) {
            const text = Editor.string(editor, editorSelection);
            if (text) {
                const id = uuid();
                setLinkId(id);
                const linkElement: LinkElement = {
                    kind: ElementKind.link,
                    children: [],
                    id: id,
                };
                Transforms.wrapNodes(editor, linkElement, { split: true });
                setIsLinkModalOpen(true);
            }
        }
    }, [editor, editorSelection, setIsLinkModalOpen]);

    const onSubmit = useCallback(
        (linkValue: string, textValue: string) => {
            const [result] = Editor.nodes(editor, {
                match: (node) =>
                    !Editor.isEditor(node) &&
                    Element.isElement(node) &&
                    node.kind === ElementKind.link &&
                    node.id === linkId,
                at: [],
            });
            const anchorTag = `<a href='${linkValue}' target="_blank">${textValue}</a>`;
            const node = result?.[0];
            const previousText = (node as LinkElement).children?.[0]?.text;
            if (previousText === anchorTag) {
                setIsLinkModalOpen(false);
                return;
            }
            const newLinkId = uuid();
            const linkElement: LinkElement = {
                kind: ElementKind.link,
                children: [{ text: anchorTag }],
                id: newLinkId,
            };
            Transforms.insertNodes(editor, linkElement, { at: result[1] });
            Transforms.removeNodes(editor, {
                match: (node) =>
                    !Editor.isEditor(node) &&
                    Element.isElement(node) &&
                    node.kind === ElementKind.link &&
                    node.id === linkId,
                at: [],
            });
            setLinkId(newLinkId);
            setIsLinkModalOpen(false);
        },
        [editor, linkId, setIsLinkModalOpen],
    );

    const onCancel = useCallback(() => {
        if (!isEditMode) {
            const [result] = Editor.nodes(editor, {
                match: (node) =>
                    !Editor.isEditor(node) &&
                    Element.isElement(node) &&
                    node.kind === ElementKind.link &&
                    node.id === linkId,
                at: [],
            });
            if (result) {
                const [node, path] = result;
                const text = Node.string(node);
                const textElement = {
                    text: text,
                };
                Transforms.insertNodes(editor, textElement, { at: path });
                Transforms.removeNodes(editor, { at: path });
            }
        }
        setIsLinkModalOpen(false);
    }, [editor, linkId, setIsLinkModalOpen, isEditMode]);

    const styles = useMemo(
        () => getStyles(componentTypes?.map ?? {}),
        [componentTypes],
    );

    return (
        <div contentEditable={false}>
            <Popper
                open={isOpen}
                anchorEl={anchorEl}
                placement="bottom-start"
                popperOptions={{
                    modifiers: [
                        {
                            name: 'flip',
                            enabled: true,
                        },
                    ],
                }}
            >
                <Container>
                    <Text>This is a ...</Text>
                    <Divider />
                    <SearchableSelect
                        options={variantTypes}
                        onChange={(field, value) =>
                            toggleVariant(value as SearchableSelectType)
                        }
                        name={'components'}
                        customStyles={styles}
                        components={customComponents}
                        menuIsOpen={true}
                        footer={
                            isLinkCreationAllowed && (
                                <FooterContent onClick={() => addLinkHandler()}>
                                    <Divider />
                                    <StyledIcon size={50} />
                                    <LinkText>Add Link</LinkText>
                                </FooterContent>
                            )
                        }
                    />
                </Container>
            </Popper>
            {isLinkModalOpen && (
                <AddLinkModal
                    selectedElement={selectedElement}
                    onSubmit={onSubmit}
                    onCancel={onCancel}
                    isEditMode={isEditMode}
                />
            )}
        </div>
    );
};

const Container = styled.div({
    height: '320px',
    width: '200px',
    border: `1px solid ${aampeTheme.colors.bordersWG01}`,
    borderRadius: aampeTheme.borderRadius.default,
    backgroundColor: aampeTheme.colors.white,
    boxShadow:
        '0px 12px 16px -4px rgba(16, 24, 40, 0.08), 0px 4px 6px -2px rgba(16, 24, 40, 0.03)',
});

const Text = styled.div({
    padding: aampeTheme.spacing(2, 3),
    fontSize: '12px',
    color: aampeTheme.colors.gray700,
});

const OptionContent = styled.div({
    display: 'flex',
    alignItems: 'center',
    gap: aampeTheme.spacing(2),
    cursor: 'pointer',
});

const OptionLabel = styled.span({
    fontSize: '12px',
    color: aampeTheme.colors.gray600,
    fontWeight: 600,
});

const FooterContent = styled.div({
    display: 'flex',
    border: `1px solid ${aampeTheme.colors.bordersWG01}`,
    borderBottomRadius: '4px',
    cursor: 'pointer',
    position: 'sticky',
});
const LinkText = styled.div({
    fontSize: '14px',
    fontWeight: 500,
    lineHeight: '18px',
    padding: aampeTheme.spacing(2, 0),
});

const StyledIcon = styled(ICONS.addLink)({
    padding: aampeTheme.spacing(2),
    transform: 'rotate(-45deg)',
});

export default CreateComponentsDropdown;
